import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { stripeService, StripeServiceError } from '../../services/stripeService';
import { useAnalyticsEvents } from '../../hooks/useAnalytics';
import { STRIPE_CONFIG, BillingInterval } from '../../config/stripe';
import { CreditCard, Package, Crown, Infinity, Users, Star, Zap, CircleCheck as CheckCircle2 } from 'lucide-react';

interface CreditPurchaseFormProps {
  onSuccess?: () => void;
}

const CreditPurchaseForm: React.FC<CreditPurchaseFormProps> = ({ onSuccess }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { trackButtonClick, trackCustomEvent } = useAnalyticsEvents();
  const [loadingCredit, setLoadingCredit] = useState(false);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('annual');

  const handlePurchase = async (type: 'credit' | 'subscription') => {
    if (!user) {
      navigate('/login');
      return;
    }

    const setLoading = type === 'credit' ? setLoadingCredit : setLoadingSubscription;
    setLoading(true);
    setError(null);

    const purchaseValue = type === 'credit'
      ? 19.90
      : (billingInterval === 'monthly' ? 29.90 : 319.90);

    trackButtonClick(`purchase_${type}_${billingInterval}`, 'credit_form');
    trackCustomEvent({
      action: type === 'credit' ? 'credit_purchase_initiated' : 'subscription_purchase_initiated',
      category: 'purchases',
      label: type === 'subscription' ? `${type}_${billingInterval}` : type,
      value: purchaseValue
    });

    try {
      let priceId: string;

      if (type === 'credit') {
        priceId = STRIPE_CONFIG.PRICES.CREDIT_UNIT;
      } else {
        priceId = billingInterval === 'monthly'
          ? STRIPE_CONFIG.PRICES.SUBSCRIPTION_MONTHLY
          : STRIPE_CONFIG.PRICES.SUBSCRIPTION_ANNUAL;
      }

      const { url } = await stripeService.createCheckoutSession({
        mode: type === 'credit' ? 'payment' : 'subscription',
        priceId,
        creditsAmount: type === 'credit' ? 1 : undefined,
        billingInterval: type === 'subscription' ? billingInterval : undefined,
        successUrl: `${window.location.origin}/credits/confirmation?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/credits/purchase`
      });

      window.location.href = url;
    } catch (err) {
      console.error('Erreur lors de l\'initialisation du paiement:', err);

      if (err instanceof StripeServiceError) {
        setError(err.message);
      } else {
        setError('Une erreur est survenue lors de l\'initialisation du paiement. Veuillez réessayer.');
      }

      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Section Titre Principal */}
      <div className="text-center mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-3">Choisissez votre formule</h2>
        <p className="text-gray-600">Que vous soyez particulier ou professionnel, nous avons la solution adaptée à vos besoins</p>
      </div>

      {/* Toggle Mensuel/Annuel - Version simplifiée */}
      <div className="text-center mb-6">
        <p className="text-sm text-gray-600 mb-3">
          Choisissez votre période
        </p>
        <div className="flex justify-center">
          <div className="relative inline-flex items-center">
            {billingInterval === 'annual' && (
              <div className="absolute -top-8 right-0 bg-green-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                -11%
              </div>
            )}

            <div className="bg-gray-100 rounded-lg p-1 inline-flex">
              <button
                onClick={() => {
                  setBillingInterval('monthly');
                  trackCustomEvent({
                    action: 'billing_period_toggled',
                    category: 'purchases',
                    label: 'monthly'
                  });
                }}
                className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                  billingInterval === 'monthly'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Mensuel
              </button>
              <button
                onClick={() => {
                  setBillingInterval('annual');
                  trackCustomEvent({
                    action: 'billing_period_toggled',
                    category: 'purchases',
                    label: 'annual'
                  });
                }}
                className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                  billingInterval === 'annual'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Annuel
              </button>
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {billingInterval === 'monthly'
            ? 'Sans engagement'
            : 'Économisez jusqu\'à 11% avec l\'abonnement annuel'}
        </p>
      </div>

      {/* Options de tarification */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Option Crédit Unitaire */}
        <div className="bg-white rounded-lg p-6 border border-gray-200 hover:border-primary/50 transition-all hover:shadow-lg">
          <div className="flex justify-center mb-4">
            <Package className="h-7 w-7 text-primary" />
          </div>

          <h3 className="text-base font-bold text-gray-900 text-center mb-2">
            Crédit Unitaire
          </h3>

          <p className="text-sm text-gray-600 text-center mb-4">
            Pour décider en connaissance de cause
          </p>

          <div className="text-center mb-4">
            <div className="text-2xl font-bold text-primary mb-1">19,90€</div>
          </div>

          <div className="space-y-2 text-sm mb-6">
            <div className="flex items-start">
              <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>1 adresse à vie</span>
            </div>
            <div className="flex items-start">
              <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>Estimations illimitées sur cette adresse</span>
            </div>
            <div className="flex items-start">
              <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>Plus de 400 types de travaux configurables</span>
            </div>
            <div className="flex items-start">
              <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>SimulReno AI inclus</span>
            </div>
            <div className="flex items-start">
              <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>Export PDF professionnel</span>
            </div>
            <div className="flex items-start">
              <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>Idéal pour dossier bancaire</span>
            </div>
          </div>

          <button
            onClick={() => handlePurchase('credit')}
            disabled={loadingCredit}
            className={`
              w-full py-3 px-4 rounded-lg font-semibold transition-all
              border border-primary text-primary hover:bg-primary hover:text-white
              ${loadingCredit ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}
            `}
          >
            {loadingCredit ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current mr-2"></div>
                Redirection...
              </div>
            ) : (
              <>Acheter (19,90€)</>
            )}
          </button>
        </div>

        {/* Option Abonnement */}
        <div className="bg-white rounded-lg p-6 border-2 border-orange-500 hover:border-orange-600 transition-all hover:shadow-lg relative">
          <div className="absolute -top-3 left-4 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center">
            <Crown className="h-3 w-3 mr-1" />
            Populaire
          </div>

          <div className="flex justify-center mb-4">
            <Crown className="h-7 w-7 text-orange-600" />
          </div>

          <h3 className="text-base font-bold text-gray-900 text-center mb-2">
            Abonnement Illimité
          </h3>

          <p className="text-sm text-gray-600 text-center mb-4">
            Pour les professionnels et investisseurs
            {billingInterval === 'annual' && ' - Économisez 11%'}
          </p>

          <div className="text-center mb-4">
            {billingInterval === 'monthly' ? (
              <>
                <div className="text-2xl font-bold text-orange-600 mb-1">29,90€</div>
                <p className="text-sm text-gray-600">TTC/mois</p>
                <p className="text-xs text-gray-500">sans engagement</p>
              </>
            ) : (
              <>
                <div className="text-2xl font-bold text-orange-600 mb-1">319,90€</div>
                <p className="text-sm text-gray-600">TTC/an</p>
                <p className="text-xs text-gray-500">soit 26,66€/mois</p>
              </>
            )}
          </div>

          <div className="space-y-2 text-sm mb-6">
            <div className="flex items-start">
              <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span><strong>Adresses illimitées</strong></span>
            </div>
            <div className="flex items-start">
              <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>Estimations illimitées sur chaque adresse</span>
            </div>
            <div className="flex items-start">
              <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>Plus de 400 types de travaux configurables</span>
            </div>
            <div className="flex items-start">
              <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>SimulReno AI inclus</span>
            </div>
            <div className="flex items-start">
              <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>Export PDF professionnel</span>
            </div>
            <div className="flex items-start">
              <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>Idéal pour dossier bancaire</span>
            </div>
            <div className="flex items-start">
              <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>Support prioritaire</span>
            </div>
            <div className="flex items-start">
              <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>Résiliation à tout moment</span>
            </div>
          </div>

          <button
            onClick={() => handlePurchase('subscription')}
            disabled={loadingSubscription}
            className={`
              w-full py-3 px-4 rounded-lg font-semibold transition-all
              bg-orange-500 text-white hover:bg-orange-600
              ${loadingSubscription ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}
            `}
          >
            {loadingSubscription ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Redirection...
              </div>
            ) : (
              <>S'abonner</>
            )}
          </button>

          <p className="text-xs text-center text-gray-500 mt-2">
            {billingInterval === 'monthly'
              ? 'Sans engagement'
              : 'Engagement annuel - Facturation en une fois'}
          </p>
        </div>
      </div>

      {/* Message d'erreur global */}
      {error && (
        <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm text-center">
          {error}
        </div>
      )}

      {/* Garanties */}
      <div className="space-y-4 text-sm text-neutral-600">
        <div className="flex items-start space-x-2">
          <div className="flex-shrink-0 mt-1">
            <svg className="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <p className="flex items-center">
              <span>Paiement sécurisé par Stripe</span>
              <CreditCard className="h-4 w-4 ml-1 text-green-500" />
            </p>
            <p className="text-xs text-gray-500 mt-1">Toutes les transactions sont cryptées et sécurisées</p>
          </div>
        </div>
        <div className="flex items-start space-x-2">
          <div className="flex-shrink-0 mt-1">
            <svg className="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <p>Résiliation libre pour l'abonnement</p>
            <p className="text-xs text-gray-500 mt-1">
              {billingInterval === 'monthly'
                ? 'Annulez à tout moment, sans frais ni engagement'
                : 'Annulez à tout moment, accès garanti jusqu\'à la fin de votre période annuelle'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditPurchaseForm;